import { Plus } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { Button } from 'core/components';
import { FileUploadType, NotificationPosition } from 'core/enums';
import { useFilesActions } from 'core/hooks';
import { UpdateDescription } from 'parts/components/PartDetails/Media/UploadFile/UpdateDescription';
import { UpdateVideo } from 'parts/components/PartDetails/Media/UploadFile/UpdateVideo';
import { UploadFile } from 'parts/components/PartDetails/Media/UploadFile/UploadFile';
import { Color } from 'theme/enums';

interface IProps {
  partNumber: string;
  manufacturerId: number;
  manufacturerName: string;
}

interface IUploadedFile {
  fileId: number;
  defaultDescription: string;
  defaultName: string;
}

export const UploadFileButton = ({ partNumber, manufacturerId, manufacturerName }: IProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [isChooserOpen, setIsChooserOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<IUploadedFile | null>(null);
  const [isVideoFormOpen, setIsVideoFormOpen] = useState(false);
  const [hasUploaded, setHasUploaded] = useState(false);

  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { fileUpload, isLoading: isUploading } = useFilesActions({
    position: NotificationPosition.FORM,
  });

  const isBusy = isUploading || hasUploaded;

  useEffect(
    () => () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    },
    []
  );

  const buildDefaults = () => ({
    defaultDescription: `${manufacturerName} - ${partNumber}`,
    defaultName: `${manufacturerName}_${partNumber}`,
  });

  const onUpload = async (file: File, fileType: FileUploadType) => {
    const { data } = await fileUpload(file, {
      fileType,
      manufacturer: manufacturerId,
      part: partNumber,
    });

    const fileId = data?.[0]?.id;

    if (!fileId) {
      return;
    }

    // Refresh the list immediately — the file is already persisted on BE, so even if the
    // user dismisses the description form, the new entry is visible (no orphan rows).
    queryClient.invalidateQueries('partMedia');

    setHasUploaded(true);

    transitionTimeoutRef.current = setTimeout(() => {
      setIsChooserOpen(false);
      setUploadedFile({ fileId, ...buildDefaults() });
      setHasUploaded(false);
    }, 1000);
  };

  const onUploadImage = (file: File) => onUpload(file, FileUploadType.PART_IMAGE);
  const onUploadDatasheet = (file: File) => onUpload(file, FileUploadType.PART_DATASHEET);

  const onDescriptionSubmitSuccess = () => {
    setUploadedFile(null);
  };

  const onDescriptionClose = () => {
    setUploadedFile(null);
  };

  const onChooseVideo = () => {
    setIsChooserOpen(false);
    setIsVideoFormOpen(true);
  };

  const onVideoSubmitSuccess = () => {
    setIsVideoFormOpen(false);
  };

  const onVideoClose = () => {
    setIsVideoFormOpen(false);
  };

  return (
    <>
      <Button
        color={Color.PRIMARY}
        text={t('labelAddFile')}
        icon={<Plus />}
        onClick={() => setIsChooserOpen(true)}
      />

      <UploadFile
        isOpen={isChooserOpen}
        disabled={isBusy}
        onClose={() => setIsChooserOpen(false)}
        onUploadImage={onUploadImage}
        onUploadDatasheet={onUploadDatasheet}
        onChooseVideo={onChooseVideo}
      />

      {uploadedFile && (
        <UpdateDescription
          isOpen
          fileId={uploadedFile.fileId}
          partNumber={partNumber}
          defaultDescription={uploadedFile.defaultDescription}
          defaultName={uploadedFile.defaultName}
          onClose={onDescriptionClose}
          onSubmitSuccess={onDescriptionSubmitSuccess}
        />
      )}

      {isVideoFormOpen && (
        <UpdateVideo
          isOpen
          partNumber={partNumber}
          onClose={onVideoClose}
          onSubmitSuccess={onVideoSubmitSuccess}
        />
      )}
    </>
  );
};
